import HexOffset from './HexOffset';
import HexCoords from './HexCoords';
import HexBearing, { Bearing } from './HexBearing';

/**
 * A collection of HexOffset objects that defines a complete targeting pattern.
 */
export default class HexOffsetSet {
  offsets: HexOffset[];

  constructor(offsets: {q: number, r: number}[]) {
    this.offsets = offsets.map(o => new HexOffset(o.q, o.r));
  }

  /**
   * Calculates the set of absolute target coordinates from a given starting position and bearing.
   * @param {HexCoords} startCoords - The absolute coordinates of the attacker.
   * @param {Bearing} startBearing - The bearing of the attacker.
   * @returns {HexCoords[]} An array of absolute HexCoords that are targeted.
   */
  getTargets(startCoords: HexCoords, startBearing: Bearing): HexCoords[] {
    return this.offsets.map(offset => {
      const rotatedOffset = offset.rotate(startBearing);
      return HexCoords.get(startCoords.q + rotatedOffset.q, startCoords.r + rotatedOffset.r);
    });
  }

  /**
   * Calculates all possible source positions (coordinates and bearing) from which an attack
   * would hit the given target coordinates.
   * @param {HexCoords} targetCoords - The absolute coordinates of the target.
   * @returns {{coords: HexCoords, bearing: Bearing}[]} An array of possible source positions.
   */
  getSources(targetCoords: HexCoords): {coords: HexCoords, bearing: Bearing}[] {
    const sources: {coords: HexCoords, bearing: Bearing}[] = [];

    for (const offset of this.offsets) {
      for (const bearing of HexBearing.all) {
        const rotatedOffset = offset.rotate(bearing);
        const sourceCoords = HexCoords.get(targetCoords.q - rotatedOffset.q, targetCoords.r - rotatedOffset.r);
        sources.push({ coords: sourceCoords, bearing });
      }
    }
    return sources;
  }

  static construct(...offsetArrays: {q: number, r: number}[][]): HexOffsetSet {
    const allOffsets = offsetArrays.flat();
    const uniqueOffsets: {q: number, r: number}[] = [];
    const seen = new Set<string>();
    for (const offset of allOffsets) {
        const key = `${offset.q},${offset.r}`;
        if (!seen.has(key)) {
            uniqueOffsets.push(offset);
            seen.add(key);
        }
    }
    return new HexOffsetSet(uniqueOffsets);
  }

  static line(bearing: Bearing, minDistance: number, maxDistance: number): {q: number, r: number}[] {
    const results = [];
    const origin = HexCoords.get(0, 0);
    for (let d = minDistance; d <= maxDistance; d++) {
        const hex = origin.neighbor(bearing, d);
        results.push({q: hex.q, r: hex.r});
    }
    return results;
  }

  static arc(distance: number, startBearing: Bearing | null = null, endBearing: Bearing | null = null): {q: number, r: number}[] {
    if (distance === 0) return [{ q: 0, r: 0 }];

    const bearings = HexBearing.all;
    const getWalkDir = (b: Bearing) => ((((b - 1) + 2) % 6) + 1) as Bearing;

    const segmentsToGenerate: Bearing[] = [];
    if (startBearing === null || endBearing === null) {
        segmentsToGenerate.push(...bearings);
    } else {
        let currentIdx = bearings.indexOf(startBearing);
        while (true) {
            segmentsToGenerate.push(bearings[currentIdx]);
            if (bearings[currentIdx] === endBearing) break;
            currentIdx = (currentIdx + 1) % 6;
        }
    }

    const results: {q: number, r: number}[] = [];
    const origin = HexCoords.get(0,0);

    const segmentsToIterate = (startBearing === null || endBearing === null)
        ? segmentsToGenerate
        : segmentsToGenerate.slice(0, -1);

    let currentHex = origin.neighbor(segmentsToGenerate[0], distance);

    for(const bearing of segmentsToIterate) {
        const walkDir = getWalkDir(bearing);
        for (let i = 0; i < distance; i++) {
            results.push({q: currentHex.q, r: currentHex.r});
            currentHex = currentHex.neighbor(walkDir);
        }
    }

    // For a partial arc, we need to include the final extremity.
    if (startBearing !== null && endBearing !== null) {
        results.push({q: currentHex.q, r: currentHex.r});
    }

    return results;
  }
}
